import Vue from 'vue'
import { Component } from 'vue-property-decorator'
import { mixins } from 'vue-class-component'
import { State } from 'vuex-class'

import { ApolloResponse } from '@/interfaces/apollo'
import { Breadcrumb } from '@/interfaces/breadcrumbs'
import { showErrors } from '@/utils/snack'
import AppBreadcrumbs from '@/components/AppBreadcrumbs.vue'
import AppSearch from '@/components/AppSearch.vue'
import AppListSearchBtn from '@/components/AppListSearchBtn.vue'
import AppTableDate from '@/components/AppTableDate.vue'
import { taskPriorityColor, taskStatusColor, taskStatus, taskPriority, taskPriorityKeys, taskStatusKeys, taskPriorityValues, taskStatusValues } from '@/modules/Task/helpers/task'
import TaskDialog from '@/modules/Task/scripts/task-dialog'
import AppRefresh from '@/components/AppRefresh.vue'
import AppTextField from '@/components/AppTextField.vue'
import AppSelector from '@/components/AppSelector.vue'
import AppDatePicker from '@/components/AppDatePicker.vue'
import AppTextSearch from '@/components/AppTextSearch.vue'
import AppCheckbox from '@/components/AppCheckbox.vue'
import ListPage from '@/mixins/list-page'
import { getTasks } from '@/modules/Task/gql/task.gql'
import { Task } from '@/modules/Task/interfaces/task'
import { dateToDisplay } from '@/utils/dateTime'
import AppFilterButton from '@/components/AppFilterButton.vue'
import AppSimpleTextField from '@/components/AppSimpleTextField.vue'
import AppSimpleSelector from '@/components/AppSimpleSelector.vue'
import AppSimpleTextSearch from '@/components/AppSimpleTextSearch.vue'
import AppSimpleDatePicker from '@/components/AppSimpleDatePicker.vue'
import { PageState } from '../../../store/modules/page'

@Component({
  components: {
    AppBreadcrumbs,
    AppSearch,
    AppTableDate,
    AppRefresh,
    AppTextField,
    AppSelector,
    AppDatePicker,
    AppTextSearch,
    AppListSearchBtn,
    AppCheckbox,
    AppFilterButton,
    AppSimpleTextField,
    AppSimpleSelector,
    AppSimpleTextSearch,
    AppSimpleDatePicker
  }
})
export default class TaskList extends mixins(TaskDialog, ListPage) {
  breadcrumbs: Breadcrumb[] = [
    {
      text: 'Task'
    }
  ]
  searchVal = ''

  windowSize = {
    w: 0,
    h: 0,
    cW: 0
  }

  taskPriorityColor = taskPriorityColor
  taskStatusColor = taskStatusColor
  taskStatus = taskStatus
  taskPriority = taskPriority
  taskPriorityValues = taskPriorityValues
  taskStatusValues = taskStatusValues

  dateToDisplay = dateToDisplay

  tasks: Task[] = []

  orderBy: {
    [key: string]: string
  } = {
    id: 'ID',
    title: 'TITLE',
    dueDate: 'DUE_DATE',
    status: 'STATUS',
    priority: 'PRIORITY',
    createdAt: 'CREATED_AT',
    updatedAt: 'UPDATED_AT'
  }

  tableColumn = [
    { field: 'status', title: 'Status', slots: { default: 'status_default' }, resizable: true, width: 130 },
    { field: 'priority', title: 'Priority', slots: { default: 'priority_default' }, resizable: true, sortable: true, width: 130 },
    { field: 'title', title: 'Title', slots: { default: 'title_default' }, resizable: true, width: 200 },
    { field: 'creator', title: 'Creator', slots: { default: 'creator_default' }, resizable: true },
    { field: 'dueDate', title: 'Due Date', slots: { default: 'duedate_default' }, resizable: true, sortable: true, width: 150 },
    { field: 'assign', title: 'Assign To', slots: { default: 'assignees_default' }, resizable: true },
    { field: 'notes', title: 'Notes', slots: { default: 'notes_default' }, resizable: true },
    { field: 'createdAt',
      title: 'Creation Date',
      slots: { default: 'created_at' },
      sortable: true,
      width: 150
    },
    { field: 'actions', title: 'Actions', slots: { default: 'action_default' }, fixed: 'right', width: 74 }
  ]

  tableLoading = false

  filterStatusOptions = ['dueToday', 'overdue', 'upcoming']

  @State(state => state.page) page!: PageState

  async created () {
    this.restorePage(this.page[this.$route.name || ''])
  }

  async mounted () {
    await this.restorePage(this.page[this.$route.name || ''])
    this.options.sortBy = 'dueDate'
    this.options.sortDesc = false
    this.filter.statusIdx = 0
    this.getTasks()
  }

  async getTasks () {
    const { sortBy, sortDesc, tablePage } = this.options
    const { currentPage, pageSize } = tablePage
    const {
      title, filterStatusIdx, statusIdx, priorityIdx, assignedToMe
    } = this.filter
    const orderBy = this.orderBy[sortBy] || 'DUE_DATE'
    this.tableLoading = true
    try {
      const result: ApolloResponse = await this.$apollo.query({
        query: getTasks,
        variables: {
          filter: {
            title,
            status: taskStatusKeys[statusIdx],
            priority: taskPriorityKeys[priorityIdx],
            dueToday: filterStatusIdx === 0,
            overdue: filterStatusIdx === 1,
            upcoming: filterStatusIdx === 2,
            assignedToMe
          },
          order: {
            orderBy: orderBy,
            asc: !sortDesc
          },
          paging: {
            offset: (currentPage - 1) * pageSize,
            limit: pageSize
          }
        }
      })
      if (!result.data) {
        showErrors(result.errors)
        return
      }
      this.tableLoading = false
      this.tasks = result.data.getTasks.tasks
      this.options.tablePage.total = result.data.getTasks.paging.totalCount
      this.storePage()
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
  }

  onResize () {
    const container = this.$refs.container as Vue & { clientWidth: number }
    this.windowSize = { w: window.innerWidth, h: window.innerHeight, cW: container.clientWidth }
  }

  routeTo (name: string, params: any) {
    this.$router.push({
      name,
      params
    })
  }

  routeToDetail (row: any) {
    return {
      name: 'TaskDetail',
      params: {
        id: row.id
      }
    }
  }

  sortTable (column: any) {
    this.options.sortBy = column.property
    this.options.sortDesc = column.order === 'desc'
    this.getTasks()
  }

  handlePageChange (pageData: any) {
    this.options.tablePage.currentPage = pageData.currentPage
    this.options.tablePage.pageSize = pageData.pageSize
    this.getTasks()
  }

  async refreshList () {
    await this.clearPage()
    await this.restorePage(this.page[this.$route.name || ''])
    this.getTasks()
  }

  onSuccessHandler () {
    this.hideTaskDialog()
    this.getTasks()
  }

  async deleteTaskConfirm () {
    await this.deleteTaskPost()
    this.getTasks()
  }
}
